import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Chip,
} from '@mui/material';
import { Appointment, Task, TaskStatus } from '../../../types';
import { Add } from '@mui/icons-material';

interface TaskManagerDialogProps {
  taskDialogOpen: boolean;
  setTaskDialogOpen: (open: boolean) => void;
  selectedAppointment: Appointment | null;
  appointmentTasks: Task[];
  formatAppointmentDateTime: (appointment: Appointment) => string;
  handleOpenTaskForm: (task?: Task) => void;
  handleUpdateTaskStatus: (taskId: string, newStatus: TaskStatus) => void;
  handleDeleteTask: (taskId: string) => void;
}

/** The task list for one appointment: add, edit, delete, and click a status to advance it. */
const TaskManagerDialog: React.FC<TaskManagerDialogProps> = ({
  taskDialogOpen,
  setTaskDialogOpen,
  selectedAppointment,
  appointmentTasks,
  formatAppointmentDateTime,
  handleOpenTaskForm,
  handleUpdateTaskStatus,
  handleDeleteTask,
}) => {
  return (
    <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Tasks - {selectedAppointment?.customerName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Appointment: {selectedAppointment && formatAppointmentDateTime(selectedAppointment)}
          </Typography>

          {/* Add New Task Button */}
          <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenTaskForm()}
                  size="small"
                >
                  Add New Task
                </Button>
          </Box>

          {/* Tasks List */}
          {appointmentTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No tasks found for this appointment.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {appointmentTasks.map((task) => (
                <Box
                  key={task.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    mb: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary">
                        {task.description}
                      </Typography>
                    )}
                    {task.assignedTo && (
                      <Typography variant="body2" color="primary">
                        Assigned to: {task.assignedTo.name}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={task.status.replace('_', ' ')}
                      color={
                        task.status === TaskStatus.COMPLETED ? 'success' :
                        task.status === TaskStatus.IN_PROGRESS ? 'primary' :
                        task.status === TaskStatus.CANCELLED ? 'error' : 'default'
                      }
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const statuses = [TaskStatus.PENDING, TaskStatus.IN_PROGRESS, TaskStatus.COMPLETED];
                        const currentIndex = statuses.indexOf(task.status);
                        const nextStatus = statuses[(currentIndex + 1) % statuses.length];
                        handleUpdateTaskStatus(task.id, nextStatus);
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenTaskForm(task)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleDeleteTask(task.id)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTaskDialogOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskManagerDialog;
